import React from 'react';
import { CheckboxGroupProps } from '../../../types/DesignSystem/CheckboxGroup';
import Checkbox from '../Checkbox/Checkbox';
import FieldTitle from '../FieldTitle/FieldTitle';
import { FieldMessages, fieldInvalid, messageIds } from '../fieldHelper';
import { useControllableState } from '../useControllableState';

/** The fallback selection: one reference, so an empty controlled group is not a new array per render. */
const NONE: Array<string | number> = [];

/**
 * CheckboxGroup (§ 371) — see the type file for why it exists.
 *
 * `RadioGroup`'s anatomy with a checkbox's semantics. The label is `FieldTitle` naming the
 * group through `labelId` → `aria-labelledby`, the hint and the error are `FieldMessages`,
 * the boxes are `Checkbox`. Two differences are ARIA's, not choices:
 * - the role is `group`, and a group may not carry `aria-required`, so a required group is
 *   DESCRIBED as required: an sr-only "Required" in its description, FileUpload's way;
 * - the value is a set. It is reported in options order whatever order the boxes were
 *   ticked, and a value the options do not list is kept: a list filtered by a search box
 *   must not drop the selections it is hiding.
 *
 * The select-all box counts ENABLED options only, and never flips a disabled one — a box
 * the user cannot change by hand must not change as a side effect either.
 */
const CheckboxGroup = React.forwardRef<HTMLDivElement, CheckboxGroupProps>(function CheckboxGroup({
  label,
  options,
  value: valueProp,
  defaultValue,
  onChange,
  selectAllLabel,
  name,
  orientation = 'vertical',
  size = 'md',
  disabled = false,
  required = false,
  error = false,
  helperText = '',
  errorMessage,
  hideLabel = false,
  labelSize,
  className = '',
  'data-testid': dataTestId,
  'aria-describedby': describedByProp,
  ...rest
}, ref) {
  const uid = React.useId();
  const labelId = `${uid}-label`;
  const helperId = `${uid}-helper`;
  const errorId = `${uid}-error`;
  const requiredId = `${uid}-required`;
  /* § 356 / § 357 — see TextField. */
  const invalid = fieldInvalid(error, errorMessage);
  const [value, setValue] = useControllableState<Array<string | number>>(valueProp, defaultValue, NONE);
  const ticked = new Set(value);
  const describedBy = [
    describedByProp,
    required ? requiredId : undefined,
    ...messageIds({ helperId, errorId }, { helperText, errorMessage }),
  ].filter(Boolean).join(' ') || undefined;

  const commit = (next: Set<string | number>) => {
    const listed = new Set(options.map((option) => option.value));
    const ordered = [
      ...options.map((option) => option.value).filter((v) => next.has(v)),
      ...value.filter((v) => !listed.has(v) && next.has(v)),
    ];
    setValue(ordered);
    onChange?.(ordered);
  };
  const toggle = (optionValue: string | number, on: boolean) => {
    const next = new Set(ticked);
    if (on) next.add(optionValue); else next.delete(optionValue);
    commit(next);
  };

  const enabled = options.filter((option) => !disabled && !option.disabled);
  const enabledOn = enabled.filter((option) => ticked.has(option.value)).length;
  const allOn = enabled.length > 0 && enabledOn === enabled.length;
  const toggleAll = (on: boolean) => {
    const next = new Set(ticked);
    enabled.forEach((option) => (on ? next.add(option.value) : next.delete(option.value)));
    commit(next);
  };

  const layout = orientation === 'horizontal' ? 'tw-flex tw-flex-wrap tw-gap-x-6 tw-gap-y-3' : 'tw-flex tw-flex-col tw-gap-3';
  const boxes = options.map((option) => (
    <Checkbox
      key={option.value}
      name={name}
      value={String(option.value)}
      label={option.label}
      size={size}
      checked={ticked.has(option.value)}
      disabled={disabled || Boolean(option.disabled)}
      onChange={(on) => toggle(option.value, on)}
      data-testid={option['data-testid'] ?? (dataTestId ? `${dataTestId}-option-${option.value}` : undefined)}
    />
  ));

  return (
    <div className={className}>
      {label !== '' && (
        <FieldTitle
          label={label}
          required={required}
          /* § 355 — hidden, not removed: it still names the group. */
          className={hideLabel ? 'tw-sr-only' : 'tw-mb-2'}
          size={labelSize}
          disabled={disabled}
          labelId={labelId}
        />
      )}
      <div
        /* rest first — the Button.tsx rule; the role and its wiring below are the component's. */
        {...rest}
        ref={ref}
        role="group"
        aria-labelledby={label !== '' ? labelId : undefined}
        aria-invalid={invalid || undefined}
        aria-describedby={describedBy}
        data-orientation={orientation}
        data-testid={dataTestId}
        className={layout}
      >
        {selectAllLabel !== undefined && (
          <Checkbox
            label={selectAllLabel}
            size={size}
            checked={allOn}
            indeterminate={enabledOn > 0 && !allOn}
            disabled={enabled.length === 0}
            onChange={(on) => toggleAll(on)}
            data-testid={dataTestId ? `${dataTestId}-select-all` : undefined}
          />
        )}
        {selectAllLabel !== undefined && orientation === 'vertical' ? (
          /* The options read as the select-all's children: indented to its label's start
             (box + gap: 20 + 12 at md, 16 + 12 at sm). */
          <div className={`${layout} ${size === 'sm' ? 'tw-pl-7' : 'tw-pl-8'}`}>{boxes}</div>
        ) : boxes}
      </div>
      {required && (
        <span id={requiredId} className="tw-sr-only" data-testid={dataTestId ? `${dataTestId}-required` : undefined}>
          Required
        </span>
      )}
      <FieldMessages
        helperId={helperId}
        errorId={errorId}
        helperText={helperText}
        errorMessage={errorMessage}
        error={error}
        className="tw-mt-2"
        data-testid={dataTestId}
      />
    </div>
  );
});

export default CheckboxGroup;
